import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useScrollToTop } from '@/hooks/useScrollToTop';

const testimonials = [
  {
    name: '林月',
    role: '资深同人收藏者',
    content: '原神本子精选是我每天必逛的平台，分类清晰、推荐精准，帮我发现了许多之前错过的优秀同人作品。编辑部真的很懂同人爱好者的心！',
    avatar: 'https://readdy.ai/api/search-image?query=anime%20style%20female%20avatar%20portrait%20with%20warm%20smile%2C%20soft%20illustration%20style%2C%20cream%20background%2C%20simple%20character%20design%2C%20gentle%20expression&width=120&height=120&seq=avatar-01&orientation=squarish',
  },
  {
    name: '星河',
    role: '同人画师',
    content: '作为创作者，能看到自己的作品被精选推荐真的很开心。平台的推荐机制很公平，只看作品质量，给很多新人画师提供了展示的舞台。',
    avatar: 'https://readdy.ai/api/search-image?query=anime%20style%20male%20avatar%20portrait%20with%20gentle%20expression%2C%20soft%20illustration%20style%2C%20cream%20background%2C%20simple%20character%20design%2C%20warm%20lighting&width=120&height=120&seq=avatar-02&orientation=squarish',
  },
  {
    name: '提瓦特旅人',
    role: '同人爱好者',
    content: '这里的文章写得特别用心，每篇推荐都像在和朋友聊天一样亲切。尤其喜欢他们做的角色专题，信息量足又不枯燥，一口气能看一整个下午。',
    avatar: 'https://readdy.ai/api/search-image?query=anime%20style%20female%20avatar%20portrait%20with%20bright%20smile%2C%20soft%20illustration%20style%2C%20cream%20background%2C%20simple%20character%20design%2C%20cheerful%20expression&width=120&height=120&seq=avatar-03&orientation=squarish',
  },
  {
    name: '璃月书虫',
    role: '小说本收藏家',
    content: '分类系统做得太好了，CP向、小说本、插画集分得清清楚楚。而且每篇推荐文章都很有深度，不是那种随便复制粘贴的，看得出编辑真的读过。',
    avatar: 'https://readdy.ai/api/search-image?query=anime%20style%20male%20avatar%20portrait%20with%20calm%20expression%2C%20soft%20illustration%20style%2C%20cream%20background%2C%20simple%20character%20design%2C%20warm%20lighting&width=120&height=120&seq=avatar-04&orientation=squarish',
  },
];

const faqItems = [
  {
    question: '原神本子精选是什么样的平台？',
    answer: '原神本子精选是一个专注于原神同人创作推荐与资讯分享的平台。我们精选各类高质量的原神同人漫画、插画本、小说本、CP向同人等作品，为同人爱好者提供最全面的创作资讯与交流空间。',
  },
  {
    question: '如何推荐自己的同人作品？',
    answer: '您可以通过本页面的联系我们表单提交您的作品推荐申请。请提供作品名称、作者信息、作品简介以及封面图片链接，我们的编辑团队会在审核后与您联系并安排推荐排期。',
  },
  {
    question: '平台上的内容是否收费？',
    answer: '原神本子精选平台上的所有资讯与推荐文章完全免费阅读。我们是一个同人爱好者交流与分享平台，不涉及任何商业交易。具体的同人作品购买请参考各创作者的官方渠道。',
  },
  {
    question: '如何获取最新的同人作品推荐？',
    answer: '您可以收藏我们的网站并定期访问，也可以关注首页的「最新更新」板块。我们每周都会更新精选推荐内容，确保您不会错过任何精彩的同人创作。',
  },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 — 原神本子精选',
  description: '原神本子精选是一个专注于原神同人创作推荐与资讯分享的平台，致力于为原神同人爱好者提供最全面、高质量的创作资讯与交流空间。',
  url: 'https://artsygen.github.io/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '原神本子精选',
    url: 'https://artsygen.github.io',
  },
  mainEntity: {
    '@type': 'Organization',
    name: '原神本子精选',
    url: 'https://artsygen.github.io',
    description: '原神同人创作精选推荐与交流平台',
    foundingDate: '2024',
  },
};

export default function AboutPage() {
  useScrollToTop();

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setSubmitting(true);
    setFormStatus('idle');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u1oktca23r0tr1aovg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试。';
        if (String(serverMsg).toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="原神本子精选是一个专注于原神同人创作推荐与资讯分享的平台，致力于为原神同人爱好者提供最全面、高质量的创作资讯与交流空间。了解我们的品牌故事、用户口碑与联系方式。"
        keywords="原神本子,关于我们,同人创作平台,联系我们,用户口碑"
        canonicalUrl="https://artsygen.github.io/about"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-6 mb-16">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-6">
              关于我们
            </h1>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-4">
              <p>
                <strong>原神本子精选</strong>是一个专注于<strong>原神同人创作推荐</strong>与资讯分享的平台。
                我们致力于为全球原神同人爱好者提供最全面、高质量的创作资讯与交流空间。
              </p>
              <p>
                自 2024 年创立以来，我们始终坚持「品质优先」的推荐理念。编辑团队由一群热爱原神、深耕同人圈的资深爱好者组成。
                每一篇推荐文章都经过精心撰写与多轮审校，确保内容既有深度又通俗易懂，让每一位读者都能在这里找到属于自己的那份感动。
              </p>
              <h2>我们的使命</h2>
              <p>
                原神的同人创作生态极为丰富，每天都有大量优秀的作品诞生。然而在海量信息中，很多优质的同人作品往往被埋没。
                我们的使命就是成为连接优秀创作者与同人爱好者的桥梁——让好作品被更多人看到，让每一位同人爱好者都能轻松发现值得收藏的佳作。
              </p>
              <h2>我们提供什么</h2>
              <ul>
                <li><strong>精选推荐</strong>：编辑团队手工甄选的高品质同人作品，涵盖漫画、插画、小说等多种形式。</li>
                <li><strong>分类浏览</strong>：按角色、类型等维度精细分类，让你轻松找到感兴趣的内容。</li>
                <li><strong>深度文章</strong>：每篇作品推荐都配有详细的创作背景介绍与赏析，不止是罗列，更是分享。</li>
                <li><strong>创作交流</strong>：为同人创作者与爱好者搭建沟通的桥梁，共同推动原神同人文化的发展。</li>
              </ul>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{item.content}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              常见问题
            </h2>
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <details
                  key={index}
                  className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden"
                >
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-foreground-800">{item.question}</span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              联系我们
            </h2>
            <div className="max-w-lg">
              <p className="text-sm text-foreground-500 mb-6">
                如果您有作品推荐、合作意向或任何问题，欢迎通过下方表单与我们取得联系。我们会在 1-3 个工作日内回复。
              </p>
              <form
                data-readdy-form=""
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                    您的称呼 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的称呼"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                    电子邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请选择或输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="website-alt-field"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {submitting ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 mt-3">{formMessage}</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 mt-3">{formMessage}</p>
                )}
              </form>
            </div>
          </section>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqItems.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: item.answer,
                },
              })),
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}